import { Modal, Pressable, StyleSheet, View } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';

import { ThemedText } from '@/components/ThemedText';
import { useScale } from '@/hooks/useScale';
import { colors } from '@/constants/colors';

type Props = {
  visible: boolean;
  title: string;
  onResume: () => void;
  onRestart: () => void;
  onCancel: () => void;
};

export default function ResumeActionsMenu({
  visible,
  title,
  onResume,
  onRestart,
  onCancel,
}: Props) {
  const scale = useScale();
  const styles = useStyles();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <View style={styles.backdrop}>
        <View style={styles.sheet}>
          <ThemedText style={styles.title}>{title}</ThemedText>
          <ThemedText style={styles.subtitle}>
            You have a paused workout here.
          </ThemedText>

          <View style={styles.buttons}>
            <Pressable
              hasTVPreferredFocus
              style={({ focused }) => [
                styles.primaryButton,
                focused && styles.primaryButtonFocused,
              ]}
              onPress={onResume}
            >
              <Ionicons
                name="play"
                size={20 * scale}
                color={colors.brand.primary}
              />
              <ThemedText style={styles.primaryButtonText}>Resume</ThemedText>
            </Pressable>

            <Pressable
              style={({ focused }) => [
                styles.secondaryButton,
                focused && styles.secondaryButtonFocused,
              ]}
              onPress={onRestart}
            >
              <Ionicons name="refresh" size={20 * scale} color="#fff" />
              <ThemedText style={styles.secondaryButtonText}>
                Start Over
              </ThemedText>
            </Pressable>

            <Pressable
              style={({ focused }) => [
                styles.ghostButton,
                focused && styles.ghostButtonFocused,
              ]}
              onPress={onCancel}
            >
              <ThemedText style={styles.ghostButtonText}>Cancel</ThemedText>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function useStyles() {
  const scale = useScale();

  return StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24 * scale,
    },
    sheet: {
      width: '100%',
      maxWidth: 520 * scale,
      backgroundColor: colors.brand.primary,
      borderRadius: 20 * scale,
      paddingVertical: 32 * scale,
      paddingHorizontal: 32 * scale,
      gap: 8 * scale,
      borderWidth: 1,
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    title: {
      color: '#fff',
      fontSize: 24 * scale,
      fontWeight: '700',
      textAlign: 'center',
    },
    subtitle: {
      color: 'rgba(255, 255, 255, 0.75)',
      fontSize: 15 * scale,
      textAlign: 'center',
      marginBottom: 16 * scale,
    },
    buttons: {
      gap: 12 * scale,
    },
    primaryButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10 * scale,
      paddingVertical: 14 * scale,
      paddingHorizontal: 24 * scale,
      backgroundColor: '#fff',
      borderRadius: 12 * scale,
      borderWidth: 2,
      borderColor: '#fff',
    },
    primaryButtonFocused: {
      borderColor: colors.brand.secondary,
      backgroundColor: 'rgba(255, 255, 255, 0.85)',
    },
    primaryButtonText: {
      color: colors.brand.primary,
      fontSize: 18 * scale,
      fontWeight: '700',
    },
    secondaryButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10 * scale,
      paddingVertical: 14 * scale,
      paddingHorizontal: 24 * scale,
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      borderRadius: 12 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
    },
    secondaryButtonFocused: {
      borderColor: '#fff',
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
    },
    secondaryButtonText: {
      color: '#fff',
      fontSize: 18 * scale,
      fontWeight: '600',
    },
    ghostButton: {
      paddingVertical: 12 * scale,
      paddingHorizontal: 24 * scale,
      borderRadius: 12 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
      alignItems: 'center',
    },
    ghostButtonFocused: {
      borderColor: 'rgba(255,255,255,0.5)',
    },
    ghostButtonText: {
      color: 'rgba(255, 255, 255, 0.75)',
      fontSize: 16 * scale,
      fontWeight: '500',
    },
  });
}
